import React, { useMemo, useRef, useState, useEffect } from 'react';
import { FileText, Upload, Download, Eye, X, Search, File, FileSpreadsheet, FileType } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { API_URL } from '../config/api';
import FloatingParticles from './FloatingParticles';
import PageHeader from './ui/PageHeader';
import EnhancedButton from './EnhancedButton';
import * as XLSX from 'xlsx';
import mammoth from 'mammoth';
import '../styles/neutral-theme.css';

const InformesPanel = () => {
  const { user } = useAuth();
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [richPreviewLoading, setRichPreviewLoading] = useState(false);
  const [richPreviewError, setRichPreviewError] = useState('');
  const [docxHtml, setDocxHtml] = useState('');
  const [xlsxPreview, setXlsxPreview] = useState(null); // { sheetName, rows: string[][] }
  const xlsxScrollRef = useRef(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadDescription, setUploadDescription] = useState('');

  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    fetchFiles();
  }, []);

  const fetchFiles = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/informes/list`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setFiles(data.files || []);
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Error en respuesta:', response.status, errorData);
      }
    } catch (error) {
      console.error('Error fetching files:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (file) => {
    setSelectedFile(file);
    const previewUrl = `${API_URL}/api/informes/preview/${file.id}`;
    setPreviewUrl(previewUrl);
    setRichPreviewError('');
    setDocxHtml('');
    setXlsxPreview(null);
  };

  const canPreviewDocx = (fileType) => fileType === 'docx';
  const canPreviewXlsx = (fileType) => fileType === 'xlsx';

  const loadRichPreview = async (file) => {
    if (!file) return;
    if (!(canPreviewDocx(file.file_type) || canPreviewXlsx(file.file_type))) return;

    setRichPreviewLoading(true);
    setRichPreviewError('');
    setDocxHtml('');
    setXlsxPreview(null);

    try {
      // Usamos download para evitar headers inline/iframe y leer como ArrayBuffer
      const resp = await fetch(`${API_URL}/api/informes/download/${file.id}`, { credentials: 'include' });
      if (!resp.ok) {
        throw new Error(`No se pudo descargar el archivo para previsualizar (HTTP ${resp.status})`);
      }

      const arrayBuffer = await resp.arrayBuffer();

      if (canPreviewXlsx(file.file_type)) {
        const workbook = XLSX.read(arrayBuffer, { type: 'array' });
        const firstSheetName = workbook.SheetNames?.[0];
        if (!firstSheetName) {
          throw new Error('El archivo Excel no contiene hojas.');
        }
        const ws = workbook.Sheets[firstSheetName];
        const grid = XLSX.utils.sheet_to_json(ws, { header: 1, raw: false });
        const rows = (grid || []).map((r) => (r || []).map((c) => (c == null ? '' : String(c))));

        // Limitar filas para rendimiento, pero permitir más columnas para navegación horizontal
        const maxRows = 200;
        const maxCols = 120;
        const limited = rows.slice(0, maxRows).map((r) => r.slice(0, maxCols));

        setXlsxPreview({ sheetName: firstSheetName, rows: limited });
      } else if (canPreviewDocx(file.file_type)) {
        const result = await mammoth.convertToHtml({ arrayBuffer });
        setDocxHtml(result?.value || '<p>(Documento vacío)</p>');
      }
    } catch (e) {
      setRichPreviewError(e?.message || 'No se pudo generar la previsualización.');
    } finally {
      setRichPreviewLoading(false);
    }
  };

  useEffect(() => {
    if (!selectedFile) return;
    loadRichPreview(selectedFile);
  }, [selectedFile]);

  const handleDownload = async (fileId, filename) => {
    try {
      const response = await fetch(`${API_URL}/api/informes/download/${fileId}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
    } catch (error) {
      console.error('Error downloading file:', error);
      alert('Error al descargar el archivo');
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadFile) {
      alert('Por favor selecciona un archivo');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      if (uploadDescription) {
        formData.append('description', uploadDescription);
      }

      const response = await fetch(`${API_URL}/api/informes/upload`, {
        method: 'POST',
        body: formData,
        credentials: 'include'
      });

      if (response.ok) {
        // Consumir respuesta (por si backend devuelve JSON)
        await response.json().catch(() => null);
        await fetchFiles();
        setShowUploadModal(false);
        setUploadFile(null);
        setUploadDescription('');
        alert('Archivo subido correctamente');
      } else {
        const error = await response.json();
        alert(error.detail || 'Error al subir el archivo');
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      alert('Error al subir el archivo');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (fileId) => {
    if (!confirm('¿Estás seguro de que quieres eliminar este archivo?')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/informes/${fileId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        await fetchFiles();
        if (selectedFile?.id === fileId) {
          setSelectedFile(null);
          setPreviewUrl(null);
        }
      } else {
        alert('Error al eliminar el archivo');
      }
    } catch (error) {
      console.error('Error deleting file:', error);
      alert('Error al eliminar el archivo');
    }
  };

  const getFileIcon = (fileType) => {
    switch (fileType) {
      case 'pdf':
        return <FileText className="w-6 h-6 text-red-500" />;
      case 'docx':
      case 'doc':
        return <File className="w-6 h-6 text-blue-500" />;
      case 'xlsx':
      case 'xls':
        return <FileSpreadsheet className="w-6 h-6 text-green-500" />;
      case 'txt':
        return <FileType className="w-6 h-6 text-gray-500" />;
      default:
        return <FileText className="w-6 h-6" />;
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  const filteredFiles = files.filter(file =>
    file.filename.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (file.description && file.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const xlsxHasData = useMemo(() => {
    return Boolean(xlsxPreview?.rows && xlsxPreview.rows.length > 0);
  }, [xlsxPreview]);

  const scrollXlsx = (dx, dy = 0) => {
    const el = xlsxScrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dx, top: dy, behavior: 'smooth' });
  };

  return (
    <div className="informes-panel dashboard-container" style={{ padding: '24px', maxWidth: '100%', overflow: 'hidden', width: '100%', boxSizing: 'border-box' }}>
      <FloatingParticles count={25} />

      <PageHeader
        title="Informes"
        subtitle="Gestión y visualización de archivos"
        icon={<FileText />}
        actions={
          isAdmin ? (
            <EnhancedButton
              size="small"
              variant="secondary"
              icon={<Upload size={16} />}
              onClick={() => setShowUploadModal(true)}
            >
              Subir archivo
            </EnhancedButton>
          ) : null
        }
      />

      {/* Buscador */}
      <div className="card card-enhanced" style={{ margin: '16px 0 24px 0' }}>
        <div style={{ position: 'relative' }}>
          <Search style={{ 
            position: 'absolute', 
            left: '16px', 
            top: '50%', 
            transform: 'translateY(-50%)',
            width: '20px',
            height: '20px',
            color: 'rgba(12, 44, 108, 0.5)',
            zIndex: 1
          }} />
          <input
            type="text"
            placeholder="Buscar archivos por nombre o descripción..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              padding: '14px 16px 14px 48px',
              border: '1px solid rgba(12, 44, 108, 0.15)',
              borderRadius: '12px',
              fontSize: '14px',
              background: '#ffffff',
              transition: 'all 0.2s ease',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
            }}
            onFocus={(e) => {
              e.currentTarget.style.borderColor = '#1356f1';
              e.currentTarget.style.boxShadow = '0 0 0 3px rgba(19, 86, 241, 0.1)';
            }}
            onBlur={(e) => {
              e.currentTarget.style.borderColor = 'rgba(12, 44, 108, 0.15)';
              e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.05)';
            }}
          />
        </div>
      </div>

      {/* Lista de archivos y previsualización */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'minmax(320px, 380px) 1fr', 
        gap: '20px', 
        alignItems: 'stretch',
        width: '100%',
        maxWidth: '100%',
        overflow: 'hidden',
        overflowX: 'hidden',
        overflowY: 'visible',
        boxSizing: 'border-box'
      }}>
        {/* Lista de archivos */}
        <div className="card card-enhanced" style={{ 
          minHeight: '420px',
          maxHeight: '70vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            marginBottom: '16px', 
            paddingBottom: '12px', 
            borderBottom: '2px solid rgba(12, 44, 108, 0.1)',
            flexShrink: 0
          }}>
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: '#0c2c6c' }}>
              Archivos ({filteredFiles.length})
            </h3>
          </div>
          
          <div style={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            {loading ? (
              <div style={{ 
                textAlign: 'center', 
                padding: '40px 20px', 
                color: 'rgba(12, 44, 108, 0.6)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '12px',
                flex: 1,
                justifyContent: 'center'
              }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  border: '3px solid rgba(19, 86, 241, 0.2)',
                  borderTopColor: '#1356f1',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }} />
                <style>{`
                  @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                  }
                `}</style>
                <span style={{ fontSize: '14px' }}>Cargando archivos...</span>
              </div>
            ) : filteredFiles.length === 0 ? (
              <div style={{ 
                textAlign: 'center', 
                padding: '40px 20px', 
                color: 'rgba(12, 44, 108, 0.6)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '12px',
                flex: 1,
                justifyContent: 'center',
                background: 'rgba(19, 86, 241, 0.02)',
                borderRadius: '10px',
                border: '1px dashed rgba(19, 86, 241, 0.2)'
              }}>
                <FileText style={{ width: '40px', height: '40px', color: 'rgba(12, 44, 108, 0.3)' }} />
                <div>
                  <p style={{ margin: '0 0 6px 0', fontWeight: 600, fontSize: '14px', color: '#0c2c6c' }}>
                    No hay archivos disponibles
                  </p>
                  <p style={{ margin: 0, fontSize: '12px', color: 'rgba(12, 44, 108, 0.6)', lineHeight: '1.5' }}>
                    {isAdmin 
                      ? 'Sube tu primer archivo usando el botón de arriba.' 
                      : 'Los archivos aparecerán aquí cuando sean subidos.'}
                  </p>
                </div>
              </div>
            ) : (
              <div style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                gap: '8px', 
                overflowY: 'auto', 
                padding: '10px',
                paddingRight: '10px',
                flex: 1,
                boxSizing: 'border-box'
              }}>
              {filteredFiles.map((file) => (
                <div
                  key={file.id}
                  onClick={() => handleFileSelect(file)}
                  style={{
                    padding: '16px',
                    border: selectedFile?.id === file.id ? '2px solid #1356f1' : '1px solid rgba(12, 44, 108, 0.15)',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    background: selectedFile?.id === file.id 
                      ? 'linear-gradient(135deg, rgba(19, 86, 241, 0.1) 0%, rgba(19, 86, 241, 0.05) 100%)' 
                      : '#ffffff',
                    transition: 'all 0.2s ease',
                    boxShadow: selectedFile?.id === file.id 
                      ? '0 4px 12px rgba(19, 86, 241, 0.15)' 
                      : '0 2px 4px rgba(0, 0, 0, 0.05)'
                  }}
                  onMouseEnter={(e) => {
                    if (selectedFile?.id !== file.id) {
                      e.currentTarget.style.borderColor = '#1356f1';
                      e.currentTarget.style.background = 'rgba(19, 86, 241, 0.05)';
                      e.currentTarget.style.boxShadow = '0 4px 8px rgba(19, 86, 241, 0.1)';
                      e.currentTarget.style.transform = 'translateY(-2px)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedFile?.id !== file.id) {
                      e.currentTarget.style.borderColor = 'rgba(12, 44, 108, 0.15)';
                      e.currentTarget.style.background = '#ffffff';
                      e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.05)';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'start', gap: '12px' }}>
                    <div style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '10px',
                      background: selectedFile?.id === file.id 
                        ? 'linear-gradient(135deg, rgba(19, 86, 241, 0.2) 0%, rgba(245, 131, 25, 0.2) 100%)'
                        : 'rgba(12, 44, 108, 0.05)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      {getFileIcon(file.file_type)}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ 
                        fontWeight: 600, 
                        fontSize: '14px', 
                        color: '#0c2c6c', 
                        marginBottom: '6px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {file.filename}
                      </div>
                      {file.description && (
                        <div style={{ 
                          fontSize: '12px', 
                          color: 'rgba(12, 44, 108, 0.6)', 
                          marginBottom: '8px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical'
                        }}>
                          {file.description}
                        </div>
                      )}
                      <div style={{ 
                        display: 'flex', 
                        gap: '10px', 
                        fontSize: '11px', 
                        color: 'rgba(12, 44, 108, 0.5)',
                        flexWrap: 'wrap'
                      }}>
                        <span style={{
                          padding: '2px 8px',
                          background: 'rgba(12, 44, 108, 0.05)',
                          borderRadius: '4px',
                          fontWeight: 500
                        }}>
                          {formatFileSize(file.file_size)}
                        </span>
                        <span style={{
                          padding: '2px 8px',
                          background: 'rgba(12, 44, 108, 0.05)',
                          borderRadius: '4px',
                          fontWeight: 500
                        }}>
                          {new Date(file.created_at).toLocaleDateString('es-ES', { 
                            day: '2-digit', 
                            month: 'short', 
                            year: 'numeric' 
                          })}
                        </span>
                      </div>
                    </div>
                    {isAdmin && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(file.id);
                        }}
                        style={{
                          padding: '6px',
                          border: 'none',
                          background: 'rgba(239, 68, 68, 0.1)',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          color: '#ef4444',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'all 0.2s ease',
                          flexShrink: 0
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = 'rgba(239, 68, 68, 0.2)';
                          e.currentTarget.style.transform = 'scale(1.1)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)';
                          e.currentTarget.style.transform = 'scale(1)';
                        }}
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
              </div>
            )}
          </div>
        </div>

        {/* Previsualización */}
        <div className="card card-enhanced" style={{ 
          minHeight: '520px',
          maxHeight: '80vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          overflowX: 'hidden',
          overflowY: 'hidden',
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box'
        }}>
          {selectedFile ? (
            <>
              {/* Header sticky: mantiene botón Descargar siempre visible */}
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'flex-start', 
                padding: '16px 16px 14px 16px',
                borderBottom: '2px solid rgba(12, 44, 108, 0.1)',
                background: '#ffffff',
                flexShrink: 0,
                position: 'sticky',
                top: 0,
                zIndex: 10
              }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                    <div style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '8px',
                      background: 'linear-gradient(135deg, rgba(19, 86, 241, 0.1) 0%, rgba(245, 131, 25, 0.1) 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      {getFileIcon(selectedFile.file_type)}
                    </div>
                    <div>
                      <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: '#0c2c6c' }}>
                        {selectedFile.filename}
                      </h3>
                      <div style={{ 
                        display: 'flex', 
                        gap: '12px', 
                        marginTop: '6px',
                        fontSize: '12px',
                        color: 'rgba(12, 44, 108, 0.6)'
                      }}>
                        <span>{formatFileSize(selectedFile.file_size)}</span>
                        <span>•</span>
                        <span>{new Date(selectedFile.created_at).toLocaleDateString('es-ES', { 
                          day: '2-digit', 
                          month: 'long', 
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}</span>
                      </div>
                    </div>
                  </div>
                  {selectedFile.description && (
                    <p style={{ 
                      margin: '12px 0 0 0', 
                      color: 'rgba(12, 44, 108, 0.7)', 
                      fontSize: '14px',
                      lineHeight: '1.6',
                      padding: '12px',
                      background: 'rgba(12, 44, 108, 0.03)',
                      borderRadius: '8px',
                      border: '1px solid rgba(12, 44, 108, 0.1)'
                    }}>
                      {selectedFile.description}
                    </p>
                  )}
                </div>
                <button
                  onClick={() => handleDownload(selectedFile.id, selectedFile.filename)}
                  style={{
                    padding: '12px 24px',
                    background: 'linear-gradient(135deg, #f58319 0%, #ff7b00 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '12px',
                    fontSize: '14px',
                    fontWeight: 700,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    boxShadow: '0 4px 12px rgba(245, 131, 25, 0.3)',
                    transition: 'all 0.3s ease',
                    flexShrink: 0
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 6px 20px rgba(245, 131, 25, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(245, 131, 25, 0.3)';
                  }}
                >
                  <Download className="w-5 h-5" />
                  Descargar
                </button>
              </div>
              
              <div style={{
                width: '100%',
                maxWidth: '100%',
                flex: 1,
                minHeight: 0,
                maxHeight: 'calc(80vh - 200px)',
                overflow: 'hidden',
                overflowX: 'hidden',
                overflowY: 'hidden',
                background: '#ffffff',
                boxShadow: 'inset 0 2px 8px rgba(0, 0, 0, 0.05)',
                boxSizing: 'border-box'
              }}>
                {richPreviewLoading ? (
                  <div style={{
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column',
                    gap: '12px',
                    color: 'rgba(12, 44, 108, 0.65)'
                  }}>
                    <div style={{
                      width: '40px',
                      height: '40px',
                      border: '3px solid rgba(19, 86, 241, 0.2)',
                      borderTopColor: '#1356f1',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }} />
                    <span style={{ fontSize: '14px' }}>Generando previsualización…</span>
                  </div>
                ) : richPreviewError ? (
                  <div style={{
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column',
                    gap: '10px',
                    padding: '24px',
                    textAlign: 'center',
                    color: 'rgba(12, 44, 108, 0.7)'
                  }}>
                    <div style={{
                      width: '64px',
                      height: '64px',
                      borderRadius: '50%',
                      background: 'rgba(239, 68, 68, 0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <FileText className="w-7 h-7" style={{ color: '#ef4444' }} />
                    </div>
                    <div style={{ fontWeight: 700, color: '#0c2c6c' }}>No se pudo previsualizar</div>
                    <div style={{ fontSize: '13px' }}>{richPreviewError}</div>
                    <div style={{ fontSize: '13px' }}>Puedes descargar el archivo para abrirlo en tu equipo.</div>
                  </div>
                ) : selectedFile.file_type === 'pdf' ? (
                  <iframe
                    src={previewUrl}
                    style={{ width: '100%', height: '100%', border: 'none' }}
                    title="Vista previa del archivo"
                  />
                ) : selectedFile.file_type === 'txt' ? (
                  <iframe
                    src={previewUrl}
                    style={{ width: '100%', height: '100%', border: 'none', background: '#ffffff' }}
                    title="Vista previa del archivo"
                  />
                ) : selectedFile.file_type === 'xlsx' && xlsxHasData ? (
                  <div style={{ width: '100%', maxWidth: '100%', height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden', overflowX: 'hidden', overflowY: 'hidden', boxSizing: 'border-box' }}>
                    {/* Barra de información y controles */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: '12px',
                      padding: '10px 12px',
                      borderBottom: '1px solid rgba(12, 44, 108, 0.1)',
                      background: '#ffffff',
                      flexShrink: 0,
                      width: '100%',
                      maxWidth: '100%',
                      boxSizing: 'border-box',
                      overflow: 'hidden'
                    }}>
                      <div style={{ fontSize: '12px', color: 'rgba(12, 44, 108, 0.65)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        Hoja: <strong>{xlsxPreview.sheetName}</strong> · Mostrando hasta 200 filas / {xlsxPreview.rows[0]?.length || 0} columnas
                      </div>
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        <button
                          type="button"
                          onClick={() => scrollXlsx(-260, 0)}
                          style={{
                            padding: '6px 10px',
                            borderRadius: '8px',
                            border: '1px solid rgba(12, 44, 108, 0.15)',
                            background: 'rgba(19, 86, 241, 0.06)',
                            color: '#0c2c6c',
                            fontSize: '12px',
                            fontWeight: 700,
                            cursor: 'pointer'
                          }}
                        >
                          ←
                        </button>
                        <button
                          type="button"
                          onClick={() => scrollXlsx(260, 0)}
                          style={{
                            padding: '6px 10px',
                            borderRadius: '8px',
                            border: '1px solid rgba(12, 44, 108, 0.15)',
                            background: 'rgba(19, 86, 241, 0.06)',
                            color: '#0c2c6c',
                            fontSize: '12px',
                            fontWeight: 700,
                            cursor: 'pointer'
                          }}
                        >
                          →
                        </button>
                        <button
                          type="button"
                          onClick={() => scrollXlsx(0, -260)}
                          style={{
                            padding: '6px 10px',
                            borderRadius: '8px',
                            border: '1px solid rgba(12, 44, 108, 0.15)',
                            background: 'rgba(19, 86, 241, 0.06)',
                            color: '#0c2c6c',
                            fontSize: '12px',
                            fontWeight: 700,
                            cursor: 'pointer'
                          }}
                        >
                          ↑
                        </button>
                        <button
                          type="button"
                          onClick={() => scrollXlsx(0, 260)}
                          style={{
                            padding: '6px 10px',
                            borderRadius: '8px',
                            border: '1px solid rgba(12, 44, 108, 0.15)',
                            background: 'rgba(19, 86, 241, 0.06)',
                            color: '#0c2c6c',
                            fontSize: '12px',
                            fontWeight: 700,
                            cursor: 'pointer'
                          }}
                        >
                          ↓
                        </button>
                      </div>
                    </div>

                    {/* Área de scroll: permite llegar hasta la última columna y fila */}
                    <div
                      ref={xlsxScrollRef}
                      className="xlsx-scroll-container"
                      style={{
                        flex: 1,
                        minHeight: 0,
                        maxHeight: '650px',
                        minHeight: '600px',
                        overflowX: 'scroll',
                        overflowY: 'auto',
                        WebkitOverflowScrolling: 'touch',
                        background: '#ffffff',
                        width: '100%',
                        maxWidth: '100%',
                        minWidth: 0,
                        boxSizing: 'border-box',
                        position: 'relative'
                      }}
                    >
                      <table style={{ width: 'max-content', minWidth: '100%', borderCollapse: 'separate', borderSpacing: 0, tableLayout: 'auto', display: 'table' }}>
                        <thead>
                          <tr>
                            {(xlsxPreview.rows[0] || []).map((cell, cIdx) => (
                              <th
                                key={cIdx}
                                style={{
                                  borderBottom: '1px solid rgba(12, 44, 108, 0.12)',
                                  borderRight: '1px solid rgba(12, 44, 108, 0.08)',
                                  padding: '10px 12px',
                                  fontSize: '12px',
                                  color: '#0c2c6c',
                                  background: 'linear-gradient(180deg, rgba(19, 86, 241, 0.08) 0%, rgba(19, 86, 241, 0.04) 100%)',
                                  fontWeight: 800,
                                  whiteSpace: 'nowrap',
                                  textAlign: 'left'
                                }}
                              >
                                {cell}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {xlsxPreview.rows.slice(1).map((row, rIdx) => (
                            <tr key={rIdx}>
                              {row.map((cell, cIdx) => (
                                <td
                                  key={cIdx}
                                  style={{
                                    borderBottom: '1px solid rgba(12, 44, 108, 0.08)',
                                    borderRight: '1px solid rgba(12, 44, 108, 0.06)',
                                    padding: '8px 12px',
                                    fontSize: '12px',
                                    color: '#0c2c6c',
                                    background: rIdx % 2 === 0 ? '#ffffff' : 'rgba(12, 44, 108, 0.02)',
                                    whiteSpace: 'nowrap'
                                  }}
                                >
                                  {cell}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : selectedFile.file_type === 'docx' && docxHtml ? (
                  <div style={{ width: '100%', maxWidth: '100%', height: '100%', overflow: 'auto', padding: '18px 20px', boxSizing: 'border-box' }}>
                    <div
                      style={{
                        fontSize: '14px',
                        lineHeight: 1.65,
                        color: '#0c2c6c',
                        maxWidth: '100%',
                        wordWrap: 'break-word',
                        overflowWrap: 'break-word'
                      }}
                      dangerouslySetInnerHTML={{ __html: docxHtml }}
                    />
                  </div>
                ) : (
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    color: 'rgba(12, 44, 108, 0.6)',
                    padding: '40px',
                    textAlign: 'center'
                  }}>
                    <div style={{
                      width: '80px',
                      height: '80px',
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, rgba(19, 86, 241, 0.1) 0%, rgba(245, 131, 25, 0.1) 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginBottom: '24px'
                    }}>
                      {getFileIcon(selectedFile.file_type)}
                    </div>
                    <p style={{ margin: '0 0 8px 0', fontSize: '16px', fontWeight: 600, color: '#0c2c6c' }}>
                      Previsualización no disponible
                    </p>
                    <p style={{ margin: 0, fontSize: '14px', color: 'rgba(12, 44, 108, 0.6)' }}>
                      {selectedFile.file_type === 'doc'
                        ? 'Los archivos .DOC (Word antiguo) requieren conversión. Descárgalo para abrirlo.'
                        : `Los archivos ${selectedFile.file_type.toUpperCase()} no se pueden previsualizar directamente en el navegador.`}
                      <br />
                      Haz clic en "Descargar" para abrir el archivo con la aplicación correspondiente.
                    </p>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              flex: 1,
              color: 'rgba(12, 44, 108, 0.5)',
              textAlign: 'center',
              padding: '40px',
              minHeight: '400px'
            }}>
              <div style={{
                width: '64px',
                height: '64px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, rgba(19, 86, 241, 0.1) 0%, rgba(245, 131, 25, 0.1) 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '16px'
              }}>
                <Eye style={{ width: '32px', height: '32px', color: 'rgba(12, 44, 108, 0.4)' }} />
              </div>
              <p style={{ margin: '0 0 6px 0', fontSize: '16px', fontWeight: 600, color: '#0c2c6c' }}>
                Selecciona un archivo
              </p>
              <p style={{ margin: 0, fontSize: '13px', color: 'rgba(12, 44, 108, 0.6)' }}>
                Haz clic en cualquier archivo de la lista para previsualizarlo
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Modal de subida */}
      {showUploadModal && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            backdropFilter: 'blur(4px)'
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowUploadModal(false);
              setUploadFile(null);
              setUploadDescription('');
            }
          }}
        >
          <div style={{
            background: '#ffffff',
            borderRadius: '20px',
            padding: '32px',
            width: '90%',
            maxWidth: '550px',
            boxShadow: '0 24px 80px rgba(0, 0, 0, 0.4)',
            border: '1px solid rgba(12, 44, 108, 0.1)',
            animation: 'modalSlideUp 0.3s ease-out'
          }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              marginBottom: '28px',
              paddingBottom: '20px',
              borderBottom: '2px solid rgba(12, 44, 108, 0.1)'
            }}>
              <div>
                <h2 style={{ margin: 0, fontSize: '22px', fontWeight: 700, color: '#0c2c6c' }}>
                  Subir Archivo de Informe
                </h2>
                <p style={{ margin: '8px 0 0 0', fontSize: '13px', color: 'rgba(12, 44, 108, 0.6)' }}>
                  Formatos permitidos: PDF, Word, Excel, TXT (máx. 50MB)
                </p>
              </div>
              <button
                onClick={() => {
                  setShowUploadModal(false);
                  setUploadFile(null);
                  setUploadDescription('');
                }}
                style={{
                  padding: '8px',
                  border: 'none',
                  background: 'rgba(12, 44, 108, 0.05)',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  color: 'rgba(12, 44, 108, 0.7)',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)';
                  e.currentTarget.style.color = '#ef4444';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(12, 44, 108, 0.05)';
                  e.currentTarget.style.color = 'rgba(12, 44, 108, 0.7)';
                }}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleUpload}>
              <div style={{ marginBottom: '24px' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '10px', 
                  fontSize: '14px', 
                  fontWeight: 600, 
                  color: '#0c2c6c' 
                }}>
                  Seleccionar Archivo *
                </label>
                <div style={{
                  border: '2px dashed rgba(19, 86, 241, 0.3)',
                  borderRadius: '12px',
                  padding: '24px',
                  textAlign: 'center',
                  background: 'rgba(19, 86, 241, 0.02)',
                  transition: 'all 0.2s ease',
                  cursor: 'pointer'
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.currentTarget.style.borderColor = '#1356f1';
                  e.currentTarget.style.background = 'rgba(19, 86, 241, 0.05)';
                }}
                onDragLeave={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(19, 86, 241, 0.3)';
                  e.currentTarget.style.background = 'rgba(19, 86, 241, 0.02)';
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  const files = e.dataTransfer.files;
                  if (files.length > 0) {
                    setUploadFile(files[0]);
                  }
                  e.currentTarget.style.borderColor = 'rgba(19, 86, 241, 0.3)';
                  e.currentTarget.style.background = 'rgba(19, 86, 241, 0.02)';
                }}
                >
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.txt"
                    onChange={(e) => setUploadFile(e.target.files[0])}
                    required
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '14px',
                      background: 'transparent',
                      cursor: 'pointer'
                    }}
                  />
                  {uploadFile && (
                    <div style={{
                      marginTop: '12px',
                      padding: '12px',
                      background: 'rgba(34, 197, 94, 0.1)',
                      borderRadius: '8px',
                      border: '1px solid rgba(34, 197, 94, 0.3)',
                      color: '#22c55e',
                      fontSize: '13px',
                      fontWeight: 600
                    }}>
                      ✓ {uploadFile.name} ({formatFileSize(uploadFile.size)})
                    </div>
                  )}
                </div>
              </div>

              <div style={{ marginBottom: '28px' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '10px', 
                  fontSize: '14px', 
                  fontWeight: 600, 
                  color: '#0c2c6c' 
                }}>
                  Descripción (opcional)
                </label>
                <textarea
                  value={uploadDescription}
                  onChange={(e) => setUploadDescription(e.target.value)}
                  rows={4}
                  placeholder="Describe el contenido del archivo..."
                  style={{
                    width: '100%',
                    padding: '14px',
                    border: '1px solid rgba(12, 44, 108, 0.15)',
                    borderRadius: '10px',
                    fontSize: '14px',
                    resize: 'vertical',
                    background: '#ffffff',
                    color: '#0c2c6c',
                    transition: 'all 0.2s ease',
                    fontFamily: 'inherit'
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = '#1356f1';
                    e.currentTarget.style.boxShadow = '0 0 0 3px rgba(19, 86, 241, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(12, 44, 108, 0.15)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                />
              </div>

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowUploadModal(false);
                    setUploadFile(null);
                    setUploadDescription('');
                  }}
                  style={{
                    padding: '12px 24px',
                    background: '#f3f4f6',
                    color: '#0c2c6c',
                    border: 'none',
                    borderRadius: '12px',
                    fontSize: '14px',
                    fontWeight: 700,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#e5e7eb';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = '#f3f4f6';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={uploading || !uploadFile}
                  style={{
                    padding: '12px 28px',
                    background: uploading ? '#9ca3af' : 'linear-gradient(135deg, #1356f1 0%, #2563eb 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '12px',
                    fontSize: '14px',
                    fontWeight: 700,
                    cursor: uploading ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    boxShadow: uploading ? 'none' : '0 4px 12px rgba(19, 86, 241, 0.3)',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    if (!uploading && uploadFile) {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 6px 20px rgba(19, 86, 241, 0.4)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!uploading && uploadFile) {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(19, 86, 241, 0.3)';
                    }
                  }}
                >
                  {uploading ? (
                    <>
                      <div style={{
                        width: '16px',
                        height: '16px',
                        border: '2px solid rgba(255, 255, 255, 0.3)',
                        borderTopColor: 'white',
                        borderRadius: '50%',
                        animation: 'spin 0.8s linear infinite'
                      }} />
                      Subiendo...
                    </>
                  ) : (
                    <>
                      <Upload className="w-5 h-5" />
                      Subir Archivo
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style>{`
        @keyframes modalSlideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default InformesPanel;
